const sanitize = function (str: string) {
    // Ensure uppercase and remove whitespace ang hyphens
    return str ? str.toUpperCase().replace(/\s/g, '').replace(/-/g, '') : '';
};

export const isUrlValid = (str: string) => {
    if (!str) return true;
    const regexp = new RegExp(/^(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z]{2,})+(?:[\/?].*)?$/gm);
    const hasCorrectFormat = regexp.test(str);
    return hasCorrectFormat;
};

export const isEmailValid = (str: string) => {
    str = sanitize(str);
    if (!str) return true;
    const regexp = new RegExp(/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/gm);
    const hasCorrectFormat = regexp.test(str);
    return hasCorrectFormat;
};

export const isSingleEmailsValid = (str: string) => {
    if (!str) return true;
    const trimmedStr = str.trim();
    if (trimmedStr.includes(' ')) {
        return false;
    }
    const emails = trimmedStr.split(';').map((email) => email.trim());
    return emails.length === 1 && isValidEmail(emails[0]);
};

export const isMultipleEmailsValid = (str: string) => {
    if (!str) return true;
    const emails = str.split(/[;\n]/).map((email) => email.trim());
    for (const email of emails) {
        if (str.includes(' ') || !isValidEmail(email)) {
            return false;
        }
    }
    return true;
};

function isValidEmail(email: string) {
    // Define a regular expression to validate the email.
    const emailRegExp = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegExp.test(email);
}

export const isDniValid = (str: string) => {
    str = sanitize(str);

    if (!str) {
        return true;
    }

    // Test format, 8 numbers and a letter
    const regexp = new RegExp(/^\d{8}[A-HJ-NP-TV-Z]$/);
    const hasCorrectFormat = regexp.test(str);

    const dniLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
    const letter = dniLetters.charAt(parseInt(str, 10) % 23);

    return hasCorrectFormat && letter.toLowerCase() === str.charAt(8).toLowerCase();
};

export const isNieValid = (str: string) => {
    str = sanitize(str);

    if (!str) {
        return true;
    }

    // Change the initial letter for the corresponding number and validate as DNI
    let niePrefix = str.charAt(0);

    switch (niePrefix) {
        case 'X':
            niePrefix = '0';
            break;
        case 'Y':
            niePrefix = '1';
            break;
        case 'Z':
            niePrefix = '2';
            break;
        default:
            return false;
    }

    return isDniValid(niePrefix + str.substring(1));
};

// eslint-disable-next-line complexity
export const isCifValid = (str: string) => {
    const cif = sanitize(str);

    if (!cif) {
        return true;
    }

    if (cif.length !== 9) {
        return false;
    }

    const letters = ['J', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
    const digits = cif.substring(1, cif.length - 1);
    const letter = cif.substring(0, 1);
    const control = cif.substring(cif.length - 1);
    let sum = 0;
    let i;
    let digit;

    if (!letter.match(/[A-Z]/)) {
        return false;
    }

    for (i = 0; i < digits.length; ++i) {
        digit = parseInt(digits[i]);

        if (isNaN(digit)) {
            return false;
        }

        if (i % 2 === 0) {
            digit *= 2;
            if (digit > 9) {
                digit = Math.floor(digit / 10) + (digit % 10);
            }

            sum += digit;
        } else {
            sum += digit;
        }
    }

    sum %= 10;
    if (sum !== 0) {
        digit = 10 - sum;
    } else {
        digit = sum;
    }

    if (letter.match(/[ABEH]/)) {
        return String(digit) === control;
    }
    if (letter.match(/[NPQRSW]/)) {
        return letters[digit] === control;
    }

    return String(digit) === control || letters[digit] === control;
};

export const isPassportValid = (str: string) => {
    str = sanitize(str);
    if (!str) {
        return true;
    }
    const regexp = new RegExp(/^(?!^0+$)[a-zA-Z0-9]{6,20}$/);
    return regexp.test(str);
};

export const isNumber = (input: string): boolean => {
    const number = Number.parseInt(input);
    if (number === 0) return true;
    return !!number;
};

export const isPostalCodeValid = (str: string): boolean => {
    str = sanitize(str);
    const regexp = new RegExp(/^\d{5}$/);
    const isCorrectFormat = regexp.test(str);
    return !str || isCorrectFormat;
};

export const isBankAccount = (str: string): boolean => {
    str = sanitize(str);
    const regexp = new RegExp(/^\d{20}$/);
    const isValidBankAccount = regexp.test(str);
    return !str || isValidBankAccount;
};

export const isIBAN = (str: string): boolean => {
    str = sanitize(str);
    const regexp = new RegExp(/^ES\d{22}$/);
    const isValidIBAN = regexp.test(str);
    return !str || isValidIBAN;
};
